import {
    ACESFilmicToneMapping,
    Group,
    MathUtils,
    PerspectiveCamera,
    PlaneGeometry,
    PMREMGenerator,
    RepeatWrapping,
    Scene,
    TextureLoader,
    Vector3,
    WebGLRenderer,
} from 'three'

import { Water } from 'three/addons/objects/Water.js'
import { Sky } from 'three/addons/objects/Sky.js'

import tsicon from './icons/ts-icon.ts'
import nixicon from './icons/nix-icon.ts'
import dockericon from './icons/docker-icon.ts'
import html5Icon from './icons/html5-icon.ts'
import css3Icon from './icons/css3-icon.ts'
import jsIcon from './icons/js-icon.ts'
import pythonIcon from './icons/python-icon.ts'
import webpackIcon from './icons/webpack-icon.ts'
import { Scroll, Steps } from './scroll.ts'
import { colorToString, mix } from './utils.ts'

import './three.css'

const BLACK_TRANSPARENT = [0, 0, 0, 0x88]
const WHITE_OPAQUE = [0xff, 0xff, 0xff, 0xff]

function addIcons(scene: Scene): Group[] {
    const icons = [
        tsicon,
        jsIcon,
        nixicon,
        dockericon,
        html5Icon,
        css3Icon,
        pythonIcon,
        webpackIcon,
    ]

    tsicon.position.set(-42.5, 20, -30)
    tsicon.scale.set(0.03, 0.03, 0.03)

    jsIcon.position.set(-22.5, 20, -30)
    jsIcon.scale.set(0.03, 0.03, 0.03)

    nixicon.position.set(90, 40, -30)
    nixicon.scale.set(0.03, 0.03, 0.03)

    dockericon.position.set(-30, 55, -30)
    dockericon.scale.set(0.02, 0.02, 0.02)

    html5Icon.position.set(-22.5, 90, -30)
    html5Icon.scale.set(0.03, 0.03, 0.03)

    css3Icon.position.set(-37.5, 90, -30)
    css3Icon.scale.set(0.03, 0.03, 0.03)

    pythonIcon.position.set(90, 80, -30)
    pythonIcon.scale.set(0.1, 0.1, 0.1)

    webpackIcon.position.set(-2.5, 20, -30)
    webpackIcon.scale.set(0.03, 0.03, 0.03)

    scene.add(...icons)

    return icons
}

function handleScroll(camera: PerspectiveCamera, icons: Group[], steps: Steps) {
    const presentationProgress = steps.presentation.progress
    const competencesProgress = steps.competences.progress
    const parcoursProgress = steps.parcours.progress

    if (presentationProgress.top < 1) {
        camera.rotation.set(0, presentationProgress.top * Math.PI, 0)
        document.body.style.setProperty(
            '--text-color',
            colorToString(
                mix(BLACK_TRANSPARENT, WHITE_OPAQUE, presentationProgress.top),
            ),
        )

        for (const icon of icons) {
            icon.visible = false
        }

        return
    }

    document.body.style.setProperty('--text-color', colorToString(WHITE_OPAQUE))

    if (competencesProgress.top < 1) {
        for (const icon of icons) {
            icon.visible = true
        }
        camera.rotation.set(0, (competencesProgress.top + 1) * Math.PI, 0)
        return
    }

    camera.rotation.set(0, Math.max(parcoursProgress.top, 0) * 2 * Math.PI, 0)

    for (const icon of icons) {
        icon.visible = parcoursProgress.top < 0.5
    }
}

function addWater(scene: Scene, renderer: WebGLRenderer): Water {
    const sun = new Vector3()

    const waterGeometry = new PlaneGeometry(10000, 10000)

    const water = new Water(waterGeometry, {
        textureWidth: 512,
        textureHeight: 512,
        waterNormals: new TextureLoader().load(
            'waternormals.jpg',
            function (texture) {
                texture.wrapS = texture.wrapT = RepeatWrapping
            },
        ),
        sunDirection: new Vector3(),
        sunColor: 0xffffff,
        waterColor: 0x001e0f,
        distortionScale: 3.7,
        fog: scene.fog !== undefined,
    })

    water.rotation.x = -Math.PI / 2

    scene.add(water)

    // Skybox

    const sky = new Sky()
    sky.scale.setScalar(10000)
    scene.add(sky)

    const skyUniforms = sky.material.uniforms

    skyUniforms['turbidity'].value = 10
    skyUniforms['rayleigh'].value = 2
    skyUniforms['mieCoefficient'].value = 0.005
    skyUniforms['mieDirectionalG'].value = 0.8

    const parameters = {
        elevation: 2,
        azimuth: 180,
    }

    const pmremGenerator = new PMREMGenerator(renderer)
    const sceneEnv = new Scene()

    let renderTarget!: any

    function updateSun() {
        const phi = MathUtils.degToRad(90 - parameters.elevation)
        const theta = MathUtils.degToRad(parameters.azimuth)

        sun.setFromSphericalCoords(1, phi, theta)

        sky.material.uniforms['sunPosition'].value.copy(sun)
        water.material.uniforms['sunDirection'].value.copy(sun).normalize()

        if (renderTarget !== undefined) renderTarget.dispose()

        sceneEnv.add(sky)
        renderTarget = pmremGenerator.fromScene(sceneEnv)
        scene.add(sky)

        scene.environment = renderTarget.texture
    }

    updateSun()

    return water
}

function createScrollWatch(): Scroll {
    const scroll = new Scroll(document.body)

    scroll.addStep(document.querySelector('header')!, 'presentation')
    scroll.addStep(document.getElementById('presentation')!, 'presentation')
    scroll.addStep(document.getElementById('competences')!, 'competences')
    scroll.addStep(document.getElementById('projets')!, 'projets')
    scroll.addStep(document.getElementById('parcours')!, 'parcours')

    return scroll
}

function setup() {
    function onWindowResize() {
        camera.aspect = window.innerWidth / window.innerHeight
        camera.updateProjectionMatrix()

        renderer.setSize(window.innerWidth, window.innerHeight)
    }

    function animate(time: number) {
        const dt = time - lastTime
        lastTime = time
        render(dt)
    }

    function render(dt: number) {
        water.material.uniforms['time'].value += (1.0 / 500) * dt

        renderer.render(scene, camera)
    }

    let lastTime: number = 0

    const container = document.getElementById('canvas')!

    const renderer = new WebGLRenderer({ antialias: true })

    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.setAnimationLoop(animate)
    renderer.toneMapping = ACESFilmicToneMapping
    renderer.toneMappingExposure = 0.5
    container.appendChild(renderer.domElement)

    const scene = new Scene()
    const icons = addIcons(scene)
    const water = addWater(scene, renderer)

    const camera = new PerspectiveCamera(
        55,
        window.innerWidth / window.innerHeight,
        1,
        20000,
    )

    const scroll = createScrollWatch()
    scroll.onChange((steps, _) => handleScroll(camera, icons, steps))

    camera.position.set(30, 30, 100)

    window.addEventListener('resize', onWindowResize)
    window.addEventListener('scroll', () => scroll.handleScroll())
    onWindowResize()
    scroll.handleScroll()
}

setup()
